/**
 * GameStateManager - Manages the overall game state and run progression
 * Tracks current floor, player pieces, relics, and run statistics
 */

import { GameState, PieceData, PieceUpgrade, Relic, FloorData, RunData, Obstacle, MapGraph, NodeType, Square, AIConfig } from '../types';
import { MapGenerator } from './MapGenerator';
import { ACT1_ELITE_IDS, ACT2_ELITE_IDS, BOSS_IDS } from './encounters/EncounterDatabase';
import { Upgrade, OfferContext } from './upgrades/UpgradeLibrary';

// Store singleton on globalThis to survive Vite HMR module re-evaluation
const SINGLETON_KEY = '__chessrogue_gsm__';

export class GameStateManager {
  private static readonly SAVE_VERSION = 2;
  private static instance: GameStateManager;
  private gameState: GameState;
  private aiConfigOverride: AIConfig | null = null;

  private constructor() {
    this.gameState = this.createNewRun();
  }

  static getInstance(): GameStateManager {
    // Check globalThis first (survives HMR)
    if ((globalThis as any)[SINGLETON_KEY]) {
      GameStateManager.instance = (globalThis as any)[SINGLETON_KEY];
    }
    if (!GameStateManager.instance) {
      GameStateManager.instance = new GameStateManager();
      (globalThis as any)[SINGLETON_KEY] = GameStateManager.instance;
    }
    return GameStateManager.instance;
  }

  /**
   * Create a new run with starting pieces
   */
  createNewRun(): GameState {
    const runData: RunData = {
      id: this.generateRunId(),
      currentFloor: 1,
      totalFloors: 12,
      seed: Math.random().toString(36).substring(7),
      startTime: Date.now(),
      floorsCleared: 0,
    };

    // Generate map for this run
    const mapData = MapGenerator.generateMap(runData.totalFloors, runData.seed);

    return {
      currentRun: runData,
      playerPieces: this.getStartingPieces(runData.seed),
      capturedPieces: [],
      relics: [],
      activeUpgrades: [],
      gold: 0,
      victories: 0,
      isRunActive: true,
      mapData,
      currentNodeId: undefined, // Player will choose starting node
    };
  }

  /**
   * Get starting chess pieces - randomized army composition and positions
   * For 6x6 board (b-g, ranks 2-7):
   * - Back rank: rank 2
   * - Pawn rank: rank 3
   */
  private getStartingPieces(seed: string): PieceData[] {
    const seededRandom = this.createSeededRandom(seed);

    // Available columns for 6x6 board (b-g)
    const availableFiles = ['b', 'c', 'd', 'e', 'f', 'g'];
    const backRank = '2';
    const pawnRank = '3';

    // Randomly select army composition
    const armyTypes = [
      { pawns: 2, knight: 1, bishop: 0, rook: 0 },  // 2 pawns, 1 knight, 1 king
      { pawns: 2, knight: 0, bishop: 1, rook: 0 },  // 2 pawns, 1 bishop, 1 king
      { pawns: 4, knight: 0, bishop: 0, rook: 0 },  // 4 pawns, 1 king
      { pawns: 0, knight: 0, bishop: 0, rook: 1 },  // 1 rook, 1 king
    ];

    const selectedArmy = armyTypes[Math.floor(seededRandom() * armyTypes.length)];

    // Shuffle available files for random placement
    const shuffledFiles = this.shuffleArray([...availableFiles], seededRandom);
    const shuffledPawnFiles = this.shuffleArray([...availableFiles], seededRandom);

    const pieces: PieceData[] = [];
    let fileIndex = 0;
    let pawnFileIndex = 0;

    // Place king (always included)
    pieces.push({
      type: 'k',
      color: 'w',
      square: `${shuffledFiles[fileIndex]}${backRank}` as Square,
      upgrades: [],
    });
    fileIndex++;

    // Place rook if in army
    if (selectedArmy.rook > 0) {
      pieces.push({
        type: 'r',
        color: 'w',
        square: `${shuffledFiles[fileIndex]}${backRank}` as Square,
        upgrades: [],
      });
      fileIndex++;
    }

    // Place bishop if in army
    if (selectedArmy.bishop > 0) {
      pieces.push({
        type: 'b',
        color: 'w',
        square: `${shuffledFiles[fileIndex]}${backRank}` as Square,
        upgrades: [],
      });
      fileIndex++;
    }

    // Place knight if in army
    if (selectedArmy.knight > 0) {
      pieces.push({
        type: 'n',
        color: 'w',
        square: `${shuffledFiles[fileIndex]}${backRank}` as Square,
        upgrades: [],
      });
      fileIndex++;
    }

    // Place pawns on second rank
    for (let i = 0; i < selectedArmy.pawns; i++) {
      pieces.push({
        type: 'p',
        color: 'w',
        square: `${shuffledPawnFiles[pawnFileIndex]}${pawnRank}` as Square,
        upgrades: [],
      });
      pawnFileIndex++;
    }

    return pieces;
  }

  /**
   * Create a seeded random function
   */
  private createSeededRandom(seed: string): () => number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash | 0;
    }

    return () => {
      hash = (hash * 9301 + 49297) % 233280;
      if (hash < 0) {
        hash += 233280;
      }
      return hash / 233280;
    };
  }

  /**
   * Shuffle array using Fisher-Yates algorithm
   */
  private shuffleArray<T>(array: T[], random: () => number): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Generate unique run ID
   */
  private generateRunId(): string {
    return `run_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  /**
   * Progress to next floor
   */
  advanceFloor(): void {
    if (!this.gameState.currentRun) return;

    this.gameState.currentRun.currentFloor++;
    this.gameState.currentRun.floorsCleared++;
  }

  /**
   * Check if run is complete
   */
  isRunComplete(): boolean {
    if (!this.gameState.currentRun) return true;
    return this.gameState.currentRun.currentFloor > this.gameState.currentRun.totalFloors;
  }

  /**
   * Get current floor number
   */
  getCurrentFloor(): number {
    return this.gameState.currentRun?.currentFloor || 1;
  }

  /**
   * Get floor data for current floor
   */
  getCurrentFloorData(): FloorData {
    const floor = this.getCurrentFloor();

    // If we have map data, use the current node's type to determine floor data
    if (this.gameState.mapData && this.gameState.currentNodeId) {
      const currentNode = this.gameState.mapData.nodes.get(this.gameState.currentNodeId);
      if (currentNode) {
        return this.generateFloorDataFromNode(currentNode.type, floor);
      }
    }

    // Fallback to floor-based generation
    return this.generateFloorData(floor);
  }

  /**
   * Generate floor data based on node type
   */
  private generateFloorDataFromNode(nodeType: NodeType, floorNumber: number): FloorData {
    let type: FloorData['type'];
    let encounter: FloorData['encounter'];

    // Map node type to floor type and encounter
    switch (nodeType) {
      case 'boss':
        type = 'boss';
        encounter = 'battle';
        break;
      case 'elite':
        type = 'elite';
        encounter = 'battle';
        break;
      case 'shop':
        type = 'event';
        encounter = 'shop';
        break;
      case 'heal':
        type = 'event';
        encounter = 'rest';
        break;
      case 'treasure':
        type = 'event';
        encounter = 'treasure';
        break;
      case 'enemy':
      default:
        type = 'combat';
        encounter = 'battle';
        break;
    }

    const encounterId = this.resolveEncounterId(nodeType, floorNumber);

    return {
      floorNumber,
      type,
      encounter,
      difficulty: this.calculateDifficulty(floorNumber),
      rewards: this.generateRewards(floorNumber, type),
      obstacles: this.generateObstacles(floorNumber),
      encounterId,
    };
  }

  /**
   * Generate floor data based on floor number
   */
  private generateFloorData(floorNumber: number): FloorData {
    // Determine floor type
    let type: FloorData['type'];
    if (floorNumber === 12) {
      type = 'boss';
    } else if (floorNumber % 4 === 0) {
      type = 'elite';
    } else if (floorNumber % 3 === 0) {
      type = 'event';
    } else {
      type = 'combat';
    }

    // Determine encounter
    let encounter: FloorData['encounter'];
    if (type === 'event') {
      const events = ['shop', 'rest', 'treasure'];
      encounter = events[Math.floor(Math.random() * events.length)] as any;
    } else {
      encounter = 'battle';
    }

    return {
      floorNumber,
      type,
      encounter,
      difficulty: this.calculateDifficulty(floorNumber),
      rewards: this.generateRewards(floorNumber, type),
      obstacles: this.generateObstacles(floorNumber),
    };
  }

  /**
   * Generate obstacles for specific floors
   */
  private generateObstacles(floor: number): Obstacle[] | undefined {
    // No obstacles until the 4th battle (floors 1-3 are complication-free)
    if (floor <= 3) {
      console.log(`[GameStateManager] Floor ${floor} - no obstacles`);
      return undefined;
    }

    // Determine board configuration
    let minRank: number, maxRank: number;
    let files: string[];

    if (floor <= 7) {
      // 6x6: columns b-g (1-6), ranks 2-7
      minRank = 2;
      maxRank = 7;
      files = ['b', 'c', 'd', 'e', 'f', 'g'];
    } else if (floor <= 11) {
      // 7x7: columns b-h (1-7), ranks 2-8
      minRank = 2;
      maxRank = 8;
      files = ['b', 'c', 'd', 'e', 'f', 'g', 'h'];
    } else {
      // 8x8: full board
      minRank = 1;
      maxRank = 8;
      files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    }

    const boardSize = files.length;
    const playerStartRank = minRank;
    const playerSecondRank = minRank + 1;

    // Create seeded random based on floor
    const seededRandom = this.createSeededRandom((this.gameState.currentRun?.seed || 'default') + floor.toString());

    // Get all squares in player starting area to avoid placing water there
    // (We can't predict exact positions due to randomization attempts, so we avoid the entire area)
    const playerStartingSquares = this.getPlayerStartingArea(files, playerStartRank, playerSecondRank);

    // Get enemy piece starting positions to avoid placing water there
    const enemyStartingSquares = this.getEnemyStartingSquares(floor);

    // Combine player and enemy squares to avoid
    const occupiedSquares = new Set([...playerStartingSquares, ...enemyStartingSquares]);

    // Determine obstacle type for this floor
    const obstacleChance = seededRandom();

    // Floors 4-5: 40% water / 40% walls / 20% none
    // Floors 6+:  30% water / 30% walls / 20% teleporters / 20% none
    if (floor >= 6) {
      if (obstacleChance < 0.3) {
        const maxWater = boardSize === 6 ? 2 : boardSize === 7 ? 3 : 4;
        return this.generateWaterObstacles(files, minRank, maxRank, occupiedSquares, maxWater, seededRandom);
      } else if (obstacleChance < 0.6) {
        return this.generateWallObstacles(files, minRank, maxRank, playerStartRank, playerSecondRank, seededRandom);
      } else if (obstacleChance < 0.8) {
        return this.generateTeleporterObstacles(files, minRank, maxRank, occupiedSquares, seededRandom);
      }
      return undefined;
    }

    if (obstacleChance < 0.4) {
      // Water obstacles
      const maxWater = boardSize === 6 ? 2 : boardSize === 7 ? 3 : 4;
      return this.generateWaterObstacles(files, minRank, maxRank, occupiedSquares, maxWater, seededRandom);
    } else if (obstacleChance < 0.8) {
      // Wall obstacles (always 4 edge walls)
      return this.generateWallObstacles(files, minRank, maxRank, playerStartRank, playerSecondRank, seededRandom);
    }

    return undefined;
  }

  /**
   * Generate a teleporter pad pair in the middle of the board, avoiding
   * starting areas. Entry on the player-facing half, exit on the enemy half.
   */
  private generateTeleporterObstacles(
    files: string[],
    minRank: number,
    maxRank: number,
    occupiedSquares: Set<string>,
    random: () => number
  ): Obstacle[] | undefined {
    const midLow = minRank + 1;
    const midHigh = maxRank - 1;
    const pickSquare = (rankLo: number, rankHi: number, exclude?: string): string | undefined => {
      for (let attempts = 0; attempts < 50; attempts++) {
        const file = files[Math.floor(random() * files.length)];
        const rank = rankLo + Math.floor(random() * (rankHi - rankLo + 1));
        const square = `${file}${rank}`;
        if (!occupiedSquares.has(square) && square !== exclude) return square;
      }
      return undefined;
    };

    const midRank = Math.floor((minRank + maxRank) / 2);
    const entry = pickSquare(midLow, midRank);
    const exit = entry ? pickSquare(midRank + 1, midHigh, entry) : undefined;
    if (!entry || !exit) return undefined;

    return [
      { square: entry as Square, type: 'teleport', pairedWith: exit as Square },
    ];
  }

  /**
   * Get the squares where enemy pieces will start for this floor
   */
  private getEnemyStartingSquares(floor: number): Set<string> {
    const squares = new Set<string>();

    // Enemy piece positions based on floor (mirrors BattleScene.getStartingFen)
    if (floor <= 7) {
      // Floor 1-7: 6x6 board (b-g, ranks 2-7)
      squares.add('b7');
      squares.add('c7');
      squares.add('d7');
      squares.add('e7');
      squares.add('f7');
      squares.add('g7');
    } else if (floor <= 11) {
      // Floor 8-11: 7x7 board (b-h, ranks 2-8)
      squares.add('b8');
      squares.add('c8');
      squares.add('d8');
      squares.add('e8');
      squares.add('f8');
      squares.add('g8');
      squares.add('h8');
      squares.add('b7');
      squares.add('c7');
      squares.add('d7');
      squares.add('e7');
      squares.add('f7');
      squares.add('g7');
      squares.add('h7');
    } else {
      // Floor 12+: Full 8x8 board
      squares.add('a8');
      squares.add('b8');
      squares.add('c8');
      squares.add('d8');
      squares.add('e8');
      squares.add('f8');
      squares.add('g8');
      squares.add('h8');
      squares.add('a7');
      squares.add('b7');
      squares.add('c7');
      squares.add('d7');
      squares.add('e7');
      squares.add('f7');
      squares.add('g7');
      squares.add('h7');
    }

    return squares;
  }

  /**
   * Get all squares in the player starting area
   * Returns ALL squares on the player's starting ranks to ensure no water is placed there
   * (We can't predict exact positions due to randomization, so we protect the entire area)
   */
  private getPlayerStartingArea(files: string[], startRank: number, secondRank: number): Set<string> {
    const squares = new Set<string>();

    // Add all squares on the back rank and second rank
    for (const file of files) {
      squares.add(`${file}${startRank}`);
      squares.add(`${file}${secondRank}`);
    }

    return squares;
  }

  /**
   * Generate randomized water obstacles
   */
  private generateWaterObstacles(
    files: string[],
    minRank: number,
    maxRank: number,
    playerStartingSquares: Set<string>,
    maxWater: number,
    random: () => number
  ): Obstacle[] {
    const obstacles: Obstacle[] = [];
    const usedSquares = new Set<string>();

    let attempts = 0;
    while (obstacles.length < maxWater && attempts < 100) {
      const file = files[Math.floor(random() * files.length)];
      const rank = minRank + Math.floor(random() * (maxRank - minRank + 1));
      const square = `${file}${rank}`;

      // Skip if already used or if a player piece starts on this square
      if (!usedSquares.has(square) && !playerStartingSquares.has(square)) {
        obstacles.push({ square: square as Square, type: 'water' });
        usedSquares.add(square);
      }

      attempts++;
    }

    return obstacles;
  }

  /**
   * Generate randomized wall obstacles
   */
  private generateWallObstacles(
    files: string[],
    minRank: number,
    maxRank: number,
    playerStartRank: number,
    playerSecondRank: number,
    random: () => number
  ): Obstacle[] {
    const obstacles: Obstacle[] = [];
    const directions: Array<'north' | 'south' | 'east' | 'west'> = ['north', 'south', 'east', 'west'];

    // Place 4 walls at random positions (edges of squares)
    let attempts = 0;
    while (obstacles.length < 4 && attempts < 100) {
      const file = files[Math.floor(random() * files.length)];
      const rank = minRank + Math.floor(random() * (maxRank - minRank + 1));
      const square = `${file}${rank}`;
      const direction = directions[Math.floor(random() * directions.length)];

      // Check if this wall would trap a piece in the starting area
      if (!this.wouldTrapPiece(square, direction, playerStartRank, playerSecondRank)) {
        obstacles.push({
          square: square as Square,
          type: 'wall',
          blocksDirection: direction
        });
      }

      attempts++;
    }

    return obstacles;
  }

  /**
   * Check if a wall would trap a piece in the starting area
   */
  private wouldTrapPiece(
    square: string,
    direction: 'north' | 'south' | 'east' | 'west',
    playerStartRank: number,
    playerSecondRank: number
  ): boolean {
    const rank = parseInt(square[1]);

    // Don't place walls that would completely block off the starting area
    // Specifically, avoid placing south-facing walls on rank playerSecondRank
    // or north-facing walls on rank playerStartRank
    if (direction === 'south' && rank === playerSecondRank) {
      return true;
    }
    if (direction === 'north' && rank === playerStartRank) {
      return true;
    }

    return false;
  }

  /**
   * Resolve which encounter ID to use for elite/boss nodes
   * Uses run seed for deterministic selection
   */
  private resolveEncounterId(nodeType: NodeType, floorNumber: number): string | undefined {
    if (nodeType !== 'elite' && nodeType !== 'boss') return undefined;

    const seed = this.gameState.currentRun?.seed || 'default';
    const seededRandom = this.createSeededRandom(seed + 'encounter' + floorNumber);

    if (nodeType === 'boss') {
      const index = Math.floor(seededRandom() * BOSS_IDS.length);
      return BOSS_IDS[index];
    }

    // Elite: pick pool based on floor number
    const pool = floorNumber <= 6 ? ACT1_ELITE_IDS : ACT2_ELITE_IDS;
    const index = Math.floor(seededRandom() * pool.length);
    return pool[index];
  }

  /**
   * Calculate difficulty based on floor
   */
  private calculateDifficulty(floor: number): 'easy' | 'medium' | 'hard' | 'expert' {
    if (floor <= 3) return 'easy';
    if (floor <= 6) return 'medium';
    if (floor <= 9) return 'hard';
    return 'expert';
  }

  /**
   * Generate rewards for floor
   */
  private generateRewards(floor: number, type: FloorData['type']): FloorData['rewards'] {
    const baseGold = 10 + floor * 5;
    const multiplier = type === 'boss' ? 3 : type === 'elite' ? 2 : 1;

    // Elites and bosses offer an extra upgrade choice (and better rarity, via
    // the tier-weighted roll in getUpgradeOffers).
    const upgradeOptions = type === 'boss' || type === 'elite' ? 4 : 3;

    return {
      gold: baseGold * multiplier,
      relicOptions: type === 'elite' || type === 'boss' ? 3 : 0,
      upgradeOptions,
    };
  }

  /**
   * Update player pieces after battle
   */
  updatePlayerPieces(pieces: PieceData[]): void {
    this.gameState.playerPieces = pieces;
  }

  /**
   * Get player pieces with relic modifiers applied
   * Deep-clones pieces and annotates them with active upgrade data
   */
  getPlayerPiecesWithRelics(): PieceData[] {
    return this.gameState.playerPieces.map(p => ({
      ...p,
      upgrades: [...p.upgrades],
    }));
  }

  /**
   * Add relic to player collection
   */
  addRelic(relic: Relic): void {
    this.gameState.relics.push(relic);
  }

  /**
   * Add an army-wide/global upgrade
   */
  addActiveUpgrade(upgrade: PieceUpgrade): void {
    if (!this.gameState.activeUpgrades) {
      this.gameState.activeUpgrades = [];
    }
    this.gameState.activeUpgrades.push(upgrade);
  }

  /**
   * Get all active upgrades
   */
  getActiveUpgrades(): PieceUpgrade[] {
    return this.gameState.activeUpgrades || [];
  }

  /**
   * Remove an active upgrade by ID
   */
  removeActiveUpgrade(id: string): void {
    if (!this.gameState.activeUpgrades) return;
    const index = this.gameState.activeUpgrades.findIndex(u => u.id === id);
    if (index !== -1) {
      this.gameState.activeUpgrades.splice(index, 1);
    }
  }

  /**
   * Debug: override the AI config used by BattleScene. Null = use floor/encounter default.
   */
  setAIConfigOverride(config: AIConfig | null): void {
    this.aiConfigOverride = config;
  }

  getAIConfigOverride(): AIConfig | null {
    return this.aiConfigOverride;
  }

  /**
   * Check if a specific upgrade value is active
   */
  hasActiveUpgrade(value: string): boolean {
    return (this.gameState.activeUpgrades || []).some(
      u => u.effect.value === value
    );
  }

  /**
   * Calculate gold earned from battle, applying per_battle and multiplier upgrades
   */
  calculateBattleGold(baseGold: number): number {
    const upgrades = this.gameState.activeUpgrades || [];

    // Sum per_battle bonuses
    let bonusGold = 0;
    for (const u of upgrades) {
      if (u.effect.type === 'gold' && u.effect.target === 'per_battle') {
        bonusGold += typeof u.effect.value === 'number' ? u.effect.value : 0;
      }
    }

    // Multiply by multiplier upgrades (stack multiplicatively)
    let multiplier = 1;
    for (const u of upgrades) {
      if (u.effect.type === 'gold' && u.effect.target === 'multiplier') {
        multiplier *= typeof u.effect.value === 'number' ? u.effect.value : 1;
      }
    }

    return Math.floor((baseGold + bonusGold) * multiplier);
  }

  /**
   * Add a new piece to player's army
   * The piece will be placed on the board at the start of the next battle
   */
  addPiece(pieceType: 'p' | 'n' | 'b' | 'r' | 'q' | 'k'): void {
    const occupiedSquares = new Set(this.gameState.playerPieces.map(p => p.square));

    // Determine which ranks/files to use based on current floor board size
    const currentFloor = this.gameState.currentRun?.currentFloor || 1;
    let ranks: number[];
    let files: string[];

    if (currentFloor <= 7) {
      // 6x6: columns b-g, ranks 2-7, player starts on 2-3
      ranks = [2, 3];
      files = ['b', 'c', 'd', 'e', 'f', 'g'];
    } else if (currentFloor <= 11) {
      // 7x7: columns b-h, ranks 2-8, player starts on 2-3
      ranks = [2, 3];
      files = ['b', 'c', 'd', 'e', 'f', 'g', 'h'];
    } else {
      // 8x8: full board, player starts on 1-2
      ranks = [1, 2];
      files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    }

    // Try to find an available square
    let availableSquare: string | undefined;
    for (const rank of ranks) {
      for (const file of files) {
        const square = `${file}${rank}`;
        if (!occupiedSquares.has(square as any)) {
          availableSquare = square;
          break;
        }
      }
      if (availableSquare) break;
    }

    if (availableSquare) {
      const newPiece: PieceData = {
        type: pieceType,
        color: 'w',
        square: availableSquare as any,
        upgrades: [],
      };

      this.gameState.playerPieces.push(newPiece);
    } else {
      console.warn('No available square to place new piece');
    }
  }

  /**
   * Promote a piece to a better type
   * Pawn -> Knight/Bishop, Knight/Bishop -> Rook, Rook -> Queen
   */
  promotePieceType(piece: PieceData, newType: 'n' | 'b' | 'r' | 'q'): void {
    const index = this.gameState.playerPieces.findIndex(p => p.square === piece.square);

    if (index !== -1) {
      this.gameState.playerPieces[index] = {
        ...this.gameState.playerPieces[index],
        type: newType,
      };
    }
  }

  /**
   * Apply a chosen upgrade to the run state. This is the single entry point
   * used by reward, shop and treasure scenes.
   */
  applyUpgrade(upgrade: Upgrade): void {
    switch (upgrade.effect.type) {
      case 'army_add': {
        const pieceChars = String(upgrade.effect.value);
        for (const ch of pieceChars) {
          if (['p', 'n', 'b', 'r', 'q', 'k'].includes(ch)) {
            this.addPiece(ch as 'p' | 'n' | 'b' | 'r' | 'q' | 'k');
          }
        }
        break;
      }

      case 'army_revive': {
        const count = typeof upgrade.effect.value === 'number' ? upgrade.effect.value : 1;
        const captured = this.gameState.capturedPieces;
        for (let i = 0; i < count && captured.length > 0; i++) {
          const randomIndex = Math.floor(Math.random() * captured.length);
          const revived = captured.splice(randomIndex, 1)[0];
          this.addPiece(revived as 'p' | 'n' | 'b' | 'r' | 'q');
        }
        break;
      }

      case 'gold':
        if (upgrade.effect.target === 'immediate') {
          this.addGold(upgrade.effect.value);
        } else {
          // per_battle and multiplier: persistent effect
          this.addActiveUpgrade({
            id: upgrade.id,
            name: upgrade.name,
            description: upgrade.description,
            effect: {
              type: 'gold',
              value: upgrade.effect.value,
              target: upgrade.effect.target,
            },
          });
        }
        break;

      case 'movement':
      case 'ability':
        this.addActiveUpgrade({
          id: upgrade.id,
          name: upgrade.name,
          description: upgrade.description,
          effect: {
            type: upgrade.effect.type as any,
            value: upgrade.effect.value,
            target: upgrade.effect.target,
          },
        });
        break;
    }

    this.saveGame();
  }

  /**
   * Build the offer context for upgrade rolls: what the player owns, the army
   * composition, and which mechanics are still relevant this run.
   */
  getOfferContext(tier: 'normal' | 'elite' | 'boss' = 'normal'): OfferContext {
    const ownedIds = new Set<string>((this.gameState.activeUpgrades || []).map(u => u.id));

    const pieceCounts = new Map<string, number>();
    for (const piece of this.gameState.playerPieces) {
      pieceCounts.set(piece.type, (pieceCounts.get(piece.type) || 0) + 1);
    }

    let puzzlesRemaining = false;
    if (this.gameState.mapData) {
      for (const node of this.gameState.mapData.nodes.values()) {
        if (node.type === 'puzzle' && !node.visited) {
          puzzlesRemaining = true;
          break;
        }
      }
    }

    return {
      floor: this.getCurrentFloor(),
      ownedIds,
      pieceCounts,
      capturedCount: this.gameState.capturedPieces.length,
      puzzlesRemaining,
      tier,
    };
  }

  /**
   * Add gold
   */
  addGold(amount: number): void {
    this.gameState.gold += amount;
    if (amount > 0 && this.gameState.currentRun) {
      this.gameState.currentRun.goldEarned = (this.gameState.currentRun.goldEarned || 0) + amount;
    }
  }

  /**
   * Record a battle victory for run statistics
   */
  recordBattleWon(): void {
    if (this.gameState.currentRun) {
      this.gameState.currentRun.battlesWon = (this.gameState.currentRun.battlesWon || 0) + 1;
    }
  }

  /**
   * Spend gold
   */
  spendGold(amount: number): boolean {
    if (this.gameState.gold >= amount) {
      this.gameState.gold -= amount;
      return true;
    }
    return false;
  }

  /**
   * Get current game state
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * Get map data for current run
   */
  getMapData(): MapGraph | undefined {
    return this.gameState.mapData;
  }

  /**
   * Get current node ID
   */
  getCurrentNodeId(): string | undefined {
    return this.gameState.currentNodeId;
  }

  /**
   * Set current node (when player selects a new node)
   */
  setCurrentNode(nodeId: string): void {
    this.gameState.currentNodeId = nodeId;

    // Mark the node as visited
    if (this.gameState.mapData) {
      const node = this.gameState.mapData.nodes.get(nodeId);
      if (node) {
        node.visited = true;
        // Advance floor based on node level
        if (this.gameState.currentRun) {
          this.gameState.currentRun.currentFloor = node.level;
        }
      }
    }

    this.saveGame();
  }

  /**
   * End current run: update lifetime stats and clear the mid-run save.
   */
  endRun(victory: boolean): void {
    if (victory) {
      this.gameState.victories++;
    }

    if (this.gameState.isRunActive) {
      const stats = this.getLifetimeStats();
      stats.runsPlayed++;
      if (victory) stats.runsWon++;
      const floorReached = this.getCurrentFloor();
      if (floorReached > stats.bestFloor) stats.bestFloor = floorReached;
      stats.totalGoldEarned += this.gameState.currentRun?.goldEarned || 0;
      this.saveLifetimeStats(stats);
    }

    this.gameState.isRunActive = false;

    // The run is over; remove the resumable save
    try {
      localStorage.removeItem('chessrogue_save');
    } catch {
      // localStorage unavailable (private mode etc.) - ignore
    }
  }

  /**
   * Lifetime statistics persisted across runs
   */
  getLifetimeStats(): { runsPlayed: number; runsWon: number; bestFloor: number; totalGoldEarned: number } {
    try {
      const raw = localStorage.getItem('chessrogue_stats');
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          runsPlayed: parsed.runsPlayed || 0,
          runsWon: parsed.runsWon || 0,
          bestFloor: parsed.bestFloor || 0,
          totalGoldEarned: parsed.totalGoldEarned || 0,
        };
      }
    } catch {
      // fall through to defaults
    }
    return { runsPlayed: 0, runsWon: 0, bestFloor: 0, totalGoldEarned: 0 };
  }

  private saveLifetimeStats(stats: { runsPlayed: number; runsWon: number; bestFloor: number; totalGoldEarned: number }): void {
    try {
      localStorage.setItem('chessrogue_stats', JSON.stringify(stats));
    } catch {
      // ignore
    }
  }

  /**
   * Whether a resumable mid-run save exists.
   */
  hasSavedRun(): boolean {
    try {
      const saved = localStorage.getItem('chessrogue_save');
      if (!saved) return false;
      const parsed = JSON.parse(saved);
      return parsed.saveVersion === GameStateManager.SAVE_VERSION && parsed.isRunActive === true;
    } catch {
      return false;
    }
  }

  /**
   * Reset for new run
   */
  startNewRun(): void {
    this.gameState = this.createNewRun();
  }

  /**
   * Save game state to localStorage
   */
  saveGame(): void {
    try {
      // Convert Map to array for JSON serialization
      const saveData = {
        ...this.gameState,
        saveVersion: GameStateManager.SAVE_VERSION,
        mapData: this.gameState.mapData
          ? {
              ...this.gameState.mapData,
              nodes: Array.from(this.gameState.mapData.nodes.entries()),
            }
          : undefined,
      };
      localStorage.setItem('chessrogue_save', JSON.stringify(saveData));
    } catch (error) {
      console.error('Failed to save game:', error);
    }
  }

  /**
   * Load game state from localStorage
   */
  loadGame(): boolean {
    try {
      const saved = localStorage.getItem('chessrogue_save');
      if (saved) {
        const loadedData = JSON.parse(saved);

        // Reject incompatible saves from older builds
        if (loadedData.saveVersion !== GameStateManager.SAVE_VERSION) {
          localStorage.removeItem('chessrogue_save');
          return false;
        }
        delete loadedData.saveVersion;

        // Convert array back to Map
        if (loadedData.mapData && Array.isArray(loadedData.mapData.nodes)) {
          loadedData.mapData.nodes = new Map(loadedData.mapData.nodes);
        }

        this.gameState = loadedData;
        return true;
      }
    } catch (error) {
      console.error('Failed to load game:', error);
    }
    return false;
  }
}
